import { defineExperiment } from '@cian/experiments/ab';
import cn from 'clsx';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { ModalFullscreen, UIHeading3 } from '@cian/ui-kit';
import { IconActionArrowLeft24 } from '@cian/ui-kit-design-tokens/icons';

import { useAuthAction } from '../../hooks/useAuthAction';

import { OfferCardMobile } from '../../components/OfferCardMobile';
import { OffersSummary } from '../../components/OffersSummary';
import { FillApplicationStickyButton } from '../FillApplicationStickyButton';
import { IMobileListingContainer } from './types';
import { useStuckState } from './utils';

import { OffersPreview } from 'shared/mortgageMiniCalculator/components/OffersPreview';
import { ETestIds } from '../../constants';
import { selectRealEstateType } from '../../selectors/calculator';
import { useTracking } from '../../utils/tracking/useTracking';
import * as styles from './styles.css';

const MobileOfferCarouselExperiment = defineExperiment('b2c_txn1226_minicalculator_offer_carousel_mobweb', {
  groups: ['A', 'B'] as const,
  default: 'A',
});

export const MobileListingContainer: React.FC<IMobileListingContainer> = ({
  offerList,
  offerListForAnalytics,
  onCardClick,
  offerSummary,
  isLoading,
}) => {
  const [isListingOpen, setIsListingOpen] = useState(false);
  const [btnRef, setBtnRef] = useState<HTMLElement | null>(null);
  const { stuck } = useStuckState(btnRef);
  const { trackShowOffers, trackClickOnTargetButton } = useTracking();
  const realEstateType = useSelector(selectRealEstateType);
  const isSecondary = realEstateType === 'secondary';
  const { isLoading: isActionLoading, handleAuthAction } = useAuthAction({
    trackingFunction: trackClickOnTargetButton,
  });

  const handleSetFormRef = useCallback((node: HTMLDivElement | null) => {
    setBtnRef(node);
  }, []);

  const handleCtaClick = () => {
    setIsListingOpen(true);
    trackShowOffers(offerListForAnalytics);
  };

  return (
    <>
      <MobileOfferCarouselExperiment>
        <MobileOfferCarouselExperiment.Group name="A">
          <OffersSummary
            onCtaClick={handleCtaClick}
            isLoading={isLoading}
            isSecondary={isSecondary}
            {...offerSummary}
          />
        </MobileOfferCarouselExperiment.Group>
        <MobileOfferCarouselExperiment.Group name="B">
          <OffersPreview
            offers={offerList}
            bankLogosPaths={offerSummary.bankLogosPaths}
            onCardClick={onCardClick}
            onCtaClick={handleCtaClick}
          />
        </MobileOfferCarouselExperiment.Group>
      </MobileOfferCarouselExperiment>
      <ModalFullscreen open={isListingOpen} onClose={() => {}}>
        <div className={styles['listing']} data-testid={ETestIds.MobileListing}>
          <div
            className={cn(styles['return-button'], stuck && styles['stuck'])}
            ref={handleSetFormRef}
            onClick={() => setIsListingOpen(false)}
            data-testid={ETestIds.MobileListingReturnButton}
          >
            <IconActionArrowLeft24 color={'icon-main-default'} />
            <UIHeading3>Ипотечные предложения</UIHeading3>
          </div>
          <div className={styles['listing-wrapper']}>
            <div className={styles['content']}>
              {offerList.map((offer, index) => {
                return <OfferCardMobile offer={offer} onClick={onCardClick(offer)} key={index} />;
              })}
            </div>
            <FillApplicationStickyButton onClick={handleAuthAction} loading={isActionLoading} />
          </div>
        </div>
      </ModalFullscreen>
    </>
  );
};
